import React, { useState, useRef, useEffect, useCallback } from 'react';
import isEqual from 'lodash.isequal';
import { DatePicker, InputGroup } from 'rsuite';
import moment from 'moment';
import { startOfDay, endOfDay } from 'date-fns';

import "./ViewDetection.scss";
import '../../styles/rsuite-no-reset.css';
import DetectionSpectrogram from './DetectionSpectrogram';
import DetectionServices from "../../services/api/DetectionServices";
import NewDetectionConfig from './NewDetectionConfig';
import { useDetectionContext } from "../../dataproviders/DetectionDataProvider";
import { useAuthContext } from '../../dataproviders/AuthDataProvider';

function ViewDetection() {
    const authContext = useAuthContext();
    const detectionContext = useDetectionContext();
    const [enableFirst, setEnableFirst] = useState(false);
    const [enableNext, setEnableNext] = useState(false);
    const [enablePrevious, setEnablePrevious] = useState(false);
    const [enableLast, setEnableLast] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, left: undefined });
    const [showDropdown, setShowDropdown] = useState(false);
    const [suggestionKeyword, setSuggestionKeyword] = useState(null);
    const [showSuggestion, setShowSuggestion] = useState(false);
    const [searchId, setSearchId] = useState('');
    const [searchList, setSearchList] = useState([]);
    const [showLoading, setShowLoading] = useState(false);
    const [linkDetection, setLinkDetection] = useState(false);
    const spectrogramRef = useRef(null);
    const [detectionParam, setDetectionParam] = useState(null);

    const [utcNow] = useState(new Date(new Date().toUTCString().slice(0, -3)));

    const handleStartDateChange = (startDate) => {
        if (detectionContext.SelectedDetectionDateRange[1] !== null && startDate >= detectionContext.SelectedDetectionDateRange[1]) {
            handleError('Warning', 'Invalid date range');
            detectionContext.SetSelectedDetectionDateRange([null, detectionContext.SelectedDetectionDateRange[1]]);
            return;
        }

        detectionContext.SetSelectedDetectionDateRange([startDate, detectionContext.SelectedDetectionDateRange[1]]);
        detectionContext.SetShowDropdown(false); // Hide the dropdown after an option is selected
        detectionContext.SetSearchId('');
        detectionContext.SetShowSuggestion(false);
    };

    const handleEndDateChange = (endDate) => {
        if (detectionContext.SelectedDetectionDateRange[0] !== null && endDate <= detectionContext.SelectedDetectionDateRange[0]) {
            handleError('Warning', 'Invalid date range');
            detectionContext.SetSelectedDetectionDateRange([detectionContext.SelectedDetectionDateRange[0], null]);
            return;
        }

        detectionContext.SetSelectedDetectionDateRange([detectionContext.SelectedDetectionDateRange[0], endDate]);
        detectionContext.SetShowDropdown(false); // Hide the dropdown after an option is selected
        detectionContext.SetSearchId('');
        detectionContext.SetShowSuggestion(false);
    };

    const handleResetDateRange = () => {
        if (detectionContext.SelectedDetectionDateRange[0] !== null || detectionContext.SelectedDetectionDateRange[1] !== null) {
            detectionContext.SetSelectedDetectionDateRange([null, null]);
            detectionContext.SetShowDropdown(false); // Hide the dropdown after an option is selected
            detectionContext.SetSearchId('');
            detectionContext.SetShowSuggestion(false);
        }
    };

    const handleMouseMove = useCallback((e) => {
        // Get the mouse coordinates relative to the parent element
        const parentRect = e.currentTarget.getBoundingClientRect();
        let x = e.clientX - parentRect.left;
        let y = e.clientY - parentRect.top;
        let left = undefined;

        // Detect the screen width
        const screenWidth = window.innerWidth;

        //applicable when screen width >= 1040px and width < 1290px
        if (screenWidth >= 1040 && screenWidth < 1290) {
            left = x + 90;
            y -= 30;
        }
        //applicable when screen width >= 1040px
        else if (screenWidth >= 934 && screenWidth < 1040) {
            left = x + 20;
            y -= 30;
        }

        // Update the tooltip position state
        setTooltipPosition({ x, y, left });
    }, []);


    useEffect(() => {
        //console.log('select tab call');
        // -1 is to get the latest detection
        if (detectionContext.ClusterLink.linkId !== -1) {
            setLinkDetection(true);
        }
    }, []);

    useEffect(() => {
        if (linkDetection) {
            if (detectionContext.ClusterLink.linkType === "cluster") {
                getDetectionDetailByClusterId(detectionContext.ClusterLink.clusterId, -1);
            }
            else {
                getDetectionDetail(0, detectionContext.ClusterLink.linkId, true);
            }
            detectionContext.SetClusterLink(prevClusterLink => ({
                ...prevClusterLink,
                linkId: -1
            }));
            setLinkDetection(false);
        }
    }, [linkDetection]);

    useEffect(() => {
        let refreshFlag = (!(detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === ''));

        if (refreshFlag) {
            if(detectionContext.DetectionIndex !== 0 && detectionContext.DetectionIndex !== 0) {
                controlNavigation(detectionContext.DetectionIndex, detectionContext.DetectionCount);
            }
        }
    }, [detectionContext.DetectionCount, detectionContext.DetectionIndex, detectionContext.DetectionConfig]);

    const controlNavigation = useCallback((index, count) => {
        //for the first time load (before that, there is no detection available)
        if (index === 0) {
            resetFilter();
            // -1 is to get the latest detection
            //console.log('call controlNavigation');
            getDetectionDetail(-1);
        }
        else if (index === 1) {
            setEnableFirst(false);
            setEnablePrevious(false);

            if (count === 1) {
                //First and only record. 1/1
                setEnableLast(false);
                setEnableNext(false);
            }
            else if (count > 1) {
                //First of X records. 1/X
                setEnableLast(true);
                setEnableNext(true);
            }
        }
        else if (index > 1) {
            setEnableFirst(true);
            setEnablePrevious(true);
            if (index < count) {
                //Middle records
                setEnableLast(true);
                setEnableNext(true);
            }
            else if (index === count) {
                //Last records
                setEnableLast(false);
                setEnableNext(false);
            }

        }
    }, []);

    const handleOptionChange = useCallback((event) => {
        const { name, value } = event.target;
        detectionContext.SetDetection((prevDetection) => ({
            ...prevDetection,
            [name]: value,
        }));
    }, []);

    const getDetection = useCallback(() => {
        getDetectionDetail(detectionContext.DetectionIndex);
    }, [detectionContext.DetectionIndex]);

    const getLastDetection = useCallback(() => {
        if (detectionContext.ClusterLink.linkType === "cluster") {
            getDetectionDetailByClusterId(detectionContext.ClusterLink.clusterId, -1);
        }
        else {
            getDetectionDetail(-1);
        }
    }, [detectionContext.DetectionCount, detectionContext.SelectedFilter]);

    const getFirstDetection = useCallback(() => {
        if (detectionContext.ClusterLink.linkType === "cluster") {
            getDetectionDetailByClusterId(detectionContext.ClusterLink.clusterId, 1);
        }
        else {
            getDetectionDetail(1);
        }
    }, [detectionContext.DetectionCount, detectionContext.SelectedFilter]);

    const getNextDetection = useCallback(() => {
        let newindex = parseInt(detectionContext.DetectionIndex) + 1

        if (detectionContext.ClusterLink.linkType === "cluster") {
            getDetectionDetailByClusterId(detectionContext.ClusterLink.clusterId, newindex);
        }
        else {
            getDetectionDetail(newindex);
        }
    }, [detectionContext.DetectionIndex]);

    const getPreviousDetection = useCallback(() => {
        let newindex = parseInt(detectionContext.DetectionIndex) - 1

        if (detectionContext.ClusterLink.linkType === "cluster") {
            getDetectionDetailByClusterId(detectionContext.ClusterLink.clusterId, newindex);
        }
        else {
            getDetectionDetail(newindex);
        }

    }, [detectionContext.DetectionIndex]);

    const getDetectionDetailByClusterId = (clusterId, rowIndex) => {
        getDetectionDetail(rowIndex, 0, false, clusterId, true);
    };

    const userOffset = new Date().getTimezoneOffset() * 60 * 1000;

    const getDetectionDetail = useCallback((rowIndex, rowId, useId, clusterId, useClusterId) => {
        //do not run if either date is null
        if ((detectionContext.SelectedDetectionDateRange[0] !== null && detectionContext.SelectedDetectionDateRange[1] !== null) ||
            (detectionContext.SelectedDetectionDateRange[0] === null && detectionContext.SelectedDetectionDateRange[1] === null)) {
            let param = {
                rowIndex: rowIndex,
                rowId: rowId,
                useId: useId,
                clusterId: clusterId,
                useClusterId: useClusterId,
                startDate: detectionContext.SelectedDetectionDateRange[0] !== null ? detectionContext.SelectedDetectionDateRange[0] : '',
                endDate: detectionContext.SelectedDetectionDateRange[1] !== null ? detectionContext.SelectedDetectionDateRange[1] : ''
            };
            //#region Set into UTC timezone

            if (param.startDate !== '') {
                // Parse the input date string
                const inputStartDate = new Date(new Date(param.startDate).getTime() - userOffset);

                // Format the date into "YYYY-MM-DDTHH:mm:ssZ" format
                const sDyear = inputStartDate.getUTCFullYear();
                const sDmonth = String(inputStartDate.getUTCMonth() + 1).padStart(2, '0');
                const sDday = String(inputStartDate.getUTCDate()).padStart(2, '0');
                const sDhours = String(inputStartDate.getUTCHours()).padStart(2, '0');
                const sDminutes = String(inputStartDate.getUTCMinutes()).padStart(2, '0');
                const sDseconds = String(inputStartDate.getUTCSeconds()).padStart(2, '0');

                const startDate = `${sDyear}-${sDmonth}-${sDday}T${sDhours}:${sDminutes}:${sDseconds}Z`;

                param.startDate = startDate;
            }

            if (param.endDate !== '') {
                // Parse the input date string
                const inputEndDate = new Date(new Date(param.endDate).getTime() - userOffset);

                // Format the date into "YYYY-MM-DDTHH:mm:ssZ" format
                const eDyear = inputEndDate.getUTCFullYear();
                const eDmonth = String(inputEndDate.getUTCMonth() + 1).padStart(2, '0');
                const eDday = String(inputEndDate.getUTCDate()).padStart(2, '0');
                const eDhours = String(inputEndDate.getUTCHours()).padStart(2, '0');
                const eDminutes = String(inputEndDate.getUTCMinutes()).padStart(2, '0');
                const eDseconds = String(inputEndDate.getUTCSeconds()).padStart(2, '0');

                const endDate = `${eDyear}-${eDmonth}-${eDday}T${eDhours}:${eDminutes}:${eDseconds}Z`;

                param.endDate = endDate;
            }

            //#endregion

            // Only update if param has changed
            if (!isEqual(detectionParam, param)) {
                setDetectionParam(param);
            }
        }

    }, [detectionContext.DetectionCount, detectionContext.SelectedFilter, detectionContext.SelectedDetectionDateRange, detectionContext.SelectedOption, detectionParam]);

    const handleError = (errorType, errorMessage) => {
        authContext.HandleError(errorType, errorMessage);
    }

    const exportWavFile = async (e) => {

        const btn = e.currentTarget;

        try {
            // Disable the button and add the loading class
            btn.disabled = true;
            btn.innerHTML = '';
            btn.classList.add('loading');

            const detectionRq = {
                detectionId: detectionContext.Detection.detectionId
            };

            const result = await DetectionServices.ExportWavFile(detectionRq);

            if (!!result) {
                let blob = result.blob;
                let fileName = result.fileName;

                // Create a download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;

                // Programmatically click the link to start the download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                handleError('Success', 'Detection has now been downloaded.');
            }
            else {
                // Display error message for network errors
                handleError('Error', 'Error occurred while downloading. Please try again.');
            }
        }
        catch (error) {
            // Display error message for network errors
            handleError('Error', 'Error occurred while downloading. Please try again.');
        }
        finally {
            // Enable the button and remove the loading class
            btn.disabled = false;
            btn.innerHTML = 'Export';
            btn.classList.remove('loading');
        }
    };

    //const submitValidation = async (e) => {

    //    const btn = e.currentTarget;

    //    try {
    //        // Disable the button and add the loading class
    //        btn.disabled = true;
    //        btn.innerHTML = '';
    //        btn.classList.add('loading');

    //        const validation = {
    //            detectionId: detectionContext.Detection.detectionId,
    //            biologicalSound: detectionContext.Detection.biologicalSound,
    //            speciesGroup: detectionContext.Detection.speciesGroup,
    //            comment: detectionContext.Detection.comment,
    //            username: sessionStorage.getItem('Neptune_Username')
    //        };

    //        const result = await DetectionServices.SaveValidation(validation);

    //        if (!!result) {
    //            handleError('Success', 'Your validation has now been saved.');
    //            detectionContext.SetCurrentValidationStatus('verified');
    //            detectionContext.SetSaveValidationClicked(true); //Refresh cluster chart
    //        }
    //        else {
    //            // Display error message for network errors
    //            handleError('Error', 'Error occurred while saving. Please try again.');
    //        }
    //    }
    //    catch (error) {
    //        // Display error message for network errors
    //        handleError('Error', 'Error occurred while saving. Please try again.');
    //    }
    //    finally {
    //        // Enable the button and remove the loading class
    //        btn.disabled = false;
    //        btn.innerHTML = 'Save';
    //        btn.classList.remove('loading');
    //    }
    //};

    const toggleDropdown = useCallback(() => {
        setShowDropdown(!showDropdown);
    }, [showDropdown]);

    const handleOptionClick = useCallback((option) => {
        detectionContext.SetSelectedFilter(option);
        setShowDropdown(false); // Hide the dropdown after an option is selected
        setSearchId('');
        setShowSuggestion(false);
    }, []);

    const isOptionSelected = useCallback((option) => {
        return option === detectionContext.SelectedFilter ? 'selected' : '';
    }, [detectionContext.SelectedFilter]);

    const renderSelectTag = useCallback((option) => {
        if (option === detectionContext.SelectedFilter) {
            return (
                <span className="select-tag float-right">
                    <i className="fa-solid fa-check"></i>
                </span>
            );
        }
        return (
            <span className="select-tag">
            </span>
        );
    }, [detectionContext.SelectedFilter]);

    const getStatus = useCallback(() => {
        let status = 2;

        if (detectionContext.SelectedFilter === "Unvalidated") {
            status = 0;
        }
        else if (detectionContext.SelectedFilter === "Validated") {
            status = 1;
        }
        else {
            status = 2;
        }
        return status;
    }, [detectionContext.SelectedFilter]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            //Go to first suggestion item if user press enter
            if (searchList.length > 0) {
                getDetectionDetail(0, searchList[0], true);
                setShowSuggestion(false);
            }
            //event.preventDefault();
        }
    }, [searchList]);

    const handleOnChange = useCallback((event) => {
        setSearchId(event.target.value);

        if ((event.target.value)) {
            setSuggestionKeyword(event.target.value);
        }
        else {
            setShowLoading((event.target.value));
            setShowSuggestion((event.target.value));
        }
    }, [detectionContext.SelectedFilter]);

    const handleSuggestion = useCallback((rowid) => {
        setSearchId(rowid);
        filterSuggestion(rowid);
        setShowSuggestion(false);
        getDetectionDetail(0, rowid, true);
    }, [searchList]);

    const filterSuggestion = useCallback((rowid) => {
        const filteredList = searchList.filter(item => item === rowid);

        // Update the state with the filtered list.
        setSearchList(filteredList);
    }, [searchList]);

    const toggleSuggestion = useCallback((event) => {
        if ((event.target.value)) {
            if (event.target.value !== searchId) {
                setSuggestionKeyword(event.target.value);
            }

            setShowSuggestion(!showSuggestion);
        }
        else {
            setShowSuggestion(false);
        }

    }, [showSuggestion]);

    useEffect(() => {

        if (suggestionKeyword != null) {
            const req = {
                keyword: suggestionKeyword,
                status: getStatus(),
                projectId: detectionContext.DetectionConfig.projectid,
                vesselId: detectionContext.DetectionConfig.vesselid,
                deviceId: detectionContext.DetectionConfig.deviceid
            };

            getSuggestion(req);
        }
    }, [suggestionKeyword]);

    const getSuggestion = useCallback(async (req) => {
        setShowLoading(true);
        setShowSuggestion(false);

        try {
            const raw = await DetectionServices.GetSearchList(req);

            if (!!raw) {
                let result = raw.data;
                setSearchList(result);
            }
            else {
                // Display error message for network errors
                handleError('Error', 'Error occurred while saving. Please try again.');
            }
        }
        catch (error) {
            // Display error message for network errors
            handleError('Error', 'Error occurred while retrieving data. Please try again.');
        }
        finally {
            setShowLoading(false);
            //to get the latest value without validate the state
            if (document.getElementById("search-input") && document.getElementById("search-input").value !== '') {
                setShowSuggestion(true);
            }
            setSuggestionKeyword(null);
        }
    }, [searchId]);

    const resetFilter = useCallback(() => {
        setShowSuggestion(false);
        setShowDropdown(false);
        setSearchId('');
    }, []);

    const closeClusterView = () => {
        detectionContext.SetClusterLink(prevClusterLink => ({
            ...prevClusterLink,
            linkType: null,
            linkId: -1,
            clusterId: -1
        }));
        getDetectionDetail(-1);
    };

    useEffect(() => {
        if (document.getElementById('detection-spectrogram-chart')) {
            //Do not run when clusterLink.linkId !== '-1' (linked from cluster chart). Prevent multiple API calls
            if (detectionContext.ClusterLink.linkId === -1) {
                // -1 is to get the latest detection
                //console.log('always call from filter selection');

                //Auto close cluster view when switch tabs
                detectionContext.SetClusterLink(prevClusterLink => ({
                    ...prevClusterLink,
                    linkType: null
                }));

                let refreshFlag = (!(detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === ''));

                if (refreshFlag) {
                    getDetectionDetail(-1);
                }
            }

            if (detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === '') {
                detectionContext.SetShowDetectionConfigPopup(true);
            }
        }
    }, [detectionContext.SelectedFilter, detectionContext.SelectedDetectionDateRange, detectionContext.DetectionConfig]);

    useEffect(() => {
        if (detectionContext.ClusterLink.linkId !== -1) {
            detectionContext.SetSelectedFilter('All'); //reset status filter
            resetFilter(); //reset search filter
            detectionContext.SetSelectedDetectionDateRange([null, null]); //reset date range filter

            setLinkDetection(true);
        }
    }, [detectionContext.ClusterLink.linkId]);

    const toggleFullScreen = () => {
        if (spectrogramRef.current) {
            if (!document.fullscreenElement) {
                spectrogramRef.current.requestFullscreen().catch(err => {
                    handleError('Error', `Error attempting to enable full-screen mode: ${err.message} (${err.name}). Please try again.`);
                });
            } else {
                document.exitFullscreen();
            }
        }
    };

    const handlePopupClick = (e) => {
        e.preventDefault(); // Prevent default anchor behavior
        detectionContext.SetShowDetectionConfigPopup(true);
    };

    return (
        <>
            <div className="detection-container position-relative">
                <NewDetectionConfig />
                <div className="spectrogram-section">
                    <div className="ms-3 me-3 mb-3">
                        <div className="detection-filter-section flex-container flex-wrap justify-content-between">
                            <a className="nav-exactlink link" href="/#/clusters"><span><i className="fa-solid fa-chevron-left fa-sm"></i>&nbsp;<span>Back to Clusters</span></span></a>
                            <div className="subsection-title text-center col-rps-light">Detection Validation</div>
                            <a className="nav-exactlink link invisible" href="/#"><span><i className="fa-solid fa-chevron-left fa-sm"></i>&nbsp;<span>Back to Clusters</span></span></a>
                        </div>
                        <div className="text-center">
                            <a className="nav-exactlink link" href="/#" onClick={handlePopupClick}><span><i className="fa-solid fa-angles-down"></i>&nbsp;<span>{detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === '' ? 'Preset Event Settings' : detectionContext.DetectionConfig.projectname + ' / ' + detectionContext.DetectionConfig.vesselname + ' / ' + detectionContext.DetectionConfig.devicename}</span></span></a>
                        </div>
                    </div>
                    <div>
                        <div className={!detectionContext.Detection.rowIndex ? "d-none" : ""}>

                            <div className="ms-3 me-3 d-flex">
                                <div className="detection-title detection-title-count me-3">
                                    <span>No. Detection: {detectionContext.Detection.rowIndex ? detectionContext.Detection.rowIndex + '/' + detectionContext.DetectionCount : ''}</span>
                                </div>
                            </div>
                            {detectionContext.ClusterLink.linkType !== "cluster" && (
                                <div className="ms-3 me-3 d-flex">
                                    <div className="detection-filter flex-container flex-wrap">
                                        <div className="detection-filter-dt me-2 mb-2">
                                            <>
                                                <InputGroup className="date-picker-input-group">
                                                    <DatePicker
                                                        className="date-picker-input"
                                                        placeholder="Start Date"
                                                        format="yyyy-MM-dd HH:mm"
                                                        calendarDefaultDate={startOfDay(utcNow)}
                                                        value={detectionContext.SelectedDetectionDateRange[0]} // Bind start date to state
                                                        onChange={handleStartDateChange} // Update start date
                                                        cleanable={false}
                                                    />
                                                    <InputGroup.Addon className="rs-input-group-addon-text">to</InputGroup.Addon>
                                                    <DatePicker
                                                        className="date-picker-input"
                                                        placeholder="End Date"
                                                        format="yyyy-MM-dd HH:mm"
                                                        calendarDefaultDate={endOfDay(utcNow)}
                                                        value={detectionContext.SelectedDetectionDateRange[1]} // Bind end date to state
                                                        onChange={handleEndDateChange} // Update end date 
                                                        cleanable={false}
                                                    />
                                                    <InputGroup.Addon className="rs-input-group-addon-text">
                                                        <i className="fa-light fa-xmark cursor-pointer" onClick={handleResetDateRange}></i>
                                                    </InputGroup.Addon>
                                                </InputGroup>
                                            </>
                                        </div>
                                        <div className="dropdown detection-filter-status me-2 mb-2">
                                            <button className={`dropbtn btn-dropdown ${showDropdown ? 'btn-dropdown-expanded' : ''}`} onClick={toggleDropdown}>
                                                <span id="ddlSelectedOption" className="btn-dropdown-text float-left">{detectionContext.SelectedFilter}</span>
                                                <span className="btn-dropdown-direction float-right"><i className={`fa-solid fa-chevron-down ${showDropdown ? 'col-rps' : ''}`}></i></span>
                                            </button>
                                            <div className={`dropdown-content dropdown-content-status me-2 mt-1 ${showDropdown ? 'd-block' : 'd-none'}`}>
                                                <div className={`dropdown-content-link ${isOptionSelected('All')}`} onClick={() => handleOptionClick('All')}><span className="">All</span>{renderSelectTag('All')}</div>
                                                <div className={`dropdown-content-link ${isOptionSelected('Validated')}`} onClick={() => handleOptionClick('Validated')}><span className="">Validated</span>{renderSelectTag('Validated')}</div>
                                                <div className={`dropdown-content-link ${isOptionSelected('Unvalidated')}`} onClick={() => handleOptionClick('Unvalidated')}><span className="">Unvalidated</span>{renderSelectTag('Unvalidated')}</div>
                                            </div>
                                        </div>
                                        <div className='detection-filter-search mb-2'>
                                            <div className='search-container d-flex'>
                                                <span className="search-icon">
                                                    <i className="fa-regular fa-magnifying-glass"></i>
                                                </span>
                                                <input type='text' id="search-input" className='search-input' maxLength='7' placeholder='Enter Detection ID'
                                                    value={searchId}
                                                    onClick={toggleSuggestion}
                                                    onChange={handleOnChange}
                                                    onKeyDown={handleKeyDown}
                                                ></input>
                                                <div className={`dropdown-content dropdown-content-search dropdown-content-search-loading ${showLoading ? 'd-block' : 'd-none'}`}>
                                                    <div className={`dropdown-content-link loading`}></div>
                                                </div>
                                                <div className={`dropdown-content dropdown-content-search ${showSuggestion ? 'd-block' : 'd-none'}`}>
                                                    {searchList.map((rowid) => (
                                                        <div key={`searchlist-${rowid}`} className="dropdown-content-item cursor-pointer" onClick={() => handleSuggestion(rowid)}>
                                                            <span className="wave-icon">
                                                                <i className="fa-solid fa-wave-triangle"></i>
                                                            </span>
                                                            <div className={`dropdown-content-link dropdown-content-link-search`}><span className="">{rowid}</span></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {detectionContext.ClusterLink.linkType === "cluster" && (
                                <div className="ms-3 me-3 d-flex mb-2">
                                    <div className="cluster-title">
                                        <span className="cluster-content">Cluster View {moment.utc(detectionContext.ClusterLink.clusterStart).format("YYYY-MM-DD HH:mm:ss")} to {moment.utc(detectionContext.ClusterLink.clusterEnd).format("YYYY-MM-DD HH:mm:ss")}</span>
                                        <span className="cluster-close-icon" onClick={() => closeClusterView()}><i className="fa-solid fa-xmark"></i></span>
                                    </div>
                                </div>
                            )}
                            <div className="detection-validation-content-container ms-3 me-3">
                                <div className="detection-spectrogram p-1" ref={spectrogramRef}>
                                    <button className="pos-absolute z-index-2" onClick={toggleFullScreen}><i className="fa-solid fa-expand"></i></button>
                                    <div className="detection-title text-center tooltip">
                                        <span className="vtext-middle">Detection ID:</span><span className="vtext-middle">{detectionContext.Detection.rowId}</span>
                                        {(detectionContext.Detection.status === "0" && detectionContext.CurrentValidationStatus !== 'verified') && <span className="tooltip-icon tooltip-link vtext-middle" onMouseMove={handleMouseMove}><i className="fa-regular fa-message-question col-orange"></i></span>}
                                        {(detectionContext.Detection.status === "1" || detectionContext.CurrentValidationStatus === 'verified') && <span className="tooltip-icon tooltip-link vtext-middle" onMouseMove={handleMouseMove}><i className="fa-regular fa-check-double col-green"></i></span>}
                                        {(detectionContext.Detection.status === "0" && detectionContext.CurrentValidationStatus !== 'verified') && <span className="tooltip-text ms-1 wspace-nowrap" style={{ top: tooltipPosition.y - 15 }}>Data is unvalidated.</span>}
                                        {(detectionContext.Detection.status === "1" || detectionContext.CurrentValidationStatus === 'verified') && <span className="tooltip-text ms-1 wspace-nowrap" style={{ top: tooltipPosition.y - 15 }}>Data is validated.</span>}
                                    </div>
                                    <div className="text-center font-small"> {detectionContext.DetectionStartDate !== '' ? detectionContext.DetectionStartDate.toLocaleDateString('en-CA', { timeZone: 'UTC' }) : ''}</div>
                                    <DetectionSpectrogram detectionParam={detectionParam} />
                                </div>
                            </div>
                            <div className="detection-navigation mt-2 mb-2 ms-3 me-3">
                                <div id="divTime" className="ms-2 detection-navigation-left tooltip">
                                    <span className="vtext-middle">{detectionContext.DetectionStartDate !== '' ? detectionContext.DetectionStartDate.toLocaleTimeString('en-GB', { timeZone: 'UTC' }) : '00:00:00'}/{detectionContext.DetectionEndDate !== '' ? detectionContext.DetectionEndDate.toLocaleTimeString('en-GB', { timeZone: 'UTC' }) : '00:00:00'}</span>
                                    <span className="tooltip-icon tooltip-link vtext-middle" onMouseMove={handleMouseMove}>
                                        <i className="fa-regular fa-circle-question"></i>
                                    </span>
                                    <span className="tooltip-text ms-14x" style={{ top: tooltipPosition.y - 15, width: 200 }}>Start and End time of a detection.</span>
                                </div>

                                <div id="divNavigation" className="detection-navigation-center text-center tooltip">
                                    <span className={`tooltip-icon vtext-middle ${enableFirst && !detectionContext.DetectionLoading ? 'tooltip-link' : 'tooltip-notlink'}`} onMouseMove={enableFirst && !detectionContext.DetectionLoading ? handleMouseMove : null} onClick={enableFirst && !detectionContext.DetectionLoading ? getFirstDetection : null}>
                                        <i className={`fa-solid fa-backward-fast detection-navigation-button ${enableFirst && !detectionContext.DetectionLoading ? 'detection-navigation-hover' : 'tooltip-notlink'}`}></i>
                                    </span>
                                    <span className="tooltip-text ms-2" style={{ top: tooltipPosition.y - 15 }}>Oldest Sample</span>
                                    <span className={`tooltip-icon vtext-middle ${enablePrevious && !detectionContext.DetectionLoading ? 'tooltip-link' : 'tooltip-notlink'}`} onMouseMove={enablePrevious && !detectionContext.DetectionLoading ? handleMouseMove : null} onClick={enablePrevious && !detectionContext.DetectionLoading ? getPreviousDetection : null}>
                                        <i className={`fa-solid fa-backward-step detection-navigation-button ${enablePrevious && !detectionContext.DetectionLoading ? 'detection-navigation-hover' : 'tooltip-notlink'}`}></i>
                                    </span>
                                    <span className="tooltip-text ms-2" style={{ top: tooltipPosition.y - 15 }}>Back Sample</span>
                                    <span className={`tooltip-icon vtext-middle ${enableNext && !detectionContext.DetectionLoading ? 'tooltip-link' : 'tooltip-notlink'}`} onMouseMove={enableNext && !detectionContext.DetectionLoading ? handleMouseMove : null} onClick={enableNext && !detectionContext.DetectionLoading ? getNextDetection : null} >
                                        <i className={`fa-solid fa-forward-step detection-navigation-button ${enableNext && !detectionContext.DetectionLoading ? 'detection-navigation-hover' : 'tooltip-notlink'}`}></i>
                                    </span>
                                    <span className="tooltip-text ms-2" style={{ top: tooltipPosition.y - 15 }}>Next Sample</span>
                                    <span className={`tooltip-icon vtext-middle ${enableLast && !detectionContext.DetectionLoading ? 'tooltip-link' : 'tooltip-notlink'}`} onMouseMove={enableLast && !detectionContext.DetectionLoading ? handleMouseMove : null} onClick={enableLast && !detectionContext.DetectionLoading ? getLastDetection : null} >
                                        <i className={`fa-solid fa-forward-fast detection-navigation-button ${enableLast && !detectionContext.DetectionLoading ? 'detection-navigation-hover' : 'tooltip-notlink'}`}></i>
                                    </span>
                                    <span className="tooltip-text ms-2" style={{ top: tooltipPosition.y - 15 }}>Newest Sample</span>
                                </div>
                            </div>
                            <div><hr className="detection-line-break" /></div>
                            <div className="detection-title tooltip mt-2 mb-2 ms-3 me-3">
                                <span className="vtext-middle">Detection Model</span>
                                <span className="tooltip-icon tooltip-link vtext-middle" onMouseMove={handleMouseMove}>
                                    <i className="fa-regular fa-circle-question"></i>
                                </span>
                                <span className="tooltip-text ms-1" style={{ top: tooltipPosition.y - 15 }}>Detection recorded by AI model.</span>
                            </div>
                            <div className="ms-3 me-3">
                                <label className="form-control-label-detection text-left wspace-nowrap">{detectionContext.Detection.maxFreq === '99999' ? 'Manual' : (detectionContext.Detection.maxFreq === '48000' ? 'Mid Frequency Model' + ((detectionContext.Detection.isAuto === 'True' || detectionContext.Detection.isAuto === '1') ? ' (Automatic)' : '') : 'Low Frequency Model' + ((detectionContext.Detection.isAuto === 'True' || detectionContext.Detection.isAuto === '1') ? ' (Automatic)' : ''))}</label>
                            </div>
                            <div><hr className="detection-line-break" /></div>
                            <div className="detection-title tooltip mt-2 mb-2 ms-3 me-3">
                                <span className="vtext-middle">Data Description</span>
                                <span className="tooltip-icon tooltip-link vtext-middle" onMouseMove={handleMouseMove}>
                                    <i className="fa-regular fa-circle-question"></i>
                                </span>
                                <span className="tooltip-text ms-1" style={{ top: tooltipPosition.y - 15 }}>Values that support the validation of the detection.</span>
                            </div>
                            <div className="ms-3 me-3 mb-0">
                                <div className="form-control-label-detection vtext-middle d-iblock">
                                    <label className="text-left">Biological Sound</label>
                                </div>
                                <div className="form-control-input-detection d-iblock">
                                    <div className="flex-container flex-wrap vtext-middle">
                                        <div className="radio-item">
                                            <span>
                                                <input id="rdBiologicalSoundAgree" type="radio" className="radio-button" name="biologicalSound" value={"True"} disabled={'True'} checked={detectionContext.Detection.biologicalSound === 'True'} onChange={handleOptionChange} />
                                            </span>
                                            <label htmlFor="rdBiologicalSoundAgree">Agree</label>
                                        </div>
                                        <div className="radio-item">
                                            <span>
                                                <input id="rdBiologicalSoundDisagree" type="radio" className="radio-button" name="biologicalSound" value={"False"} disabled={'True'} checked={detectionContext.Detection.biologicalSound ? detectionContext.Detection.biologicalSound === 'False' : true} onChange={handleOptionChange} />
                                            </span>
                                            <label htmlFor="rdBiologicalSoundDisagree">Disagree</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ms-2 me-2"><hr className="detection-detail-line-break" /></div>
                            <div className="ms-3 me-3">
                                <div className="form-control-label-detection vtext-middle d-iblock">
                                    <label className="text-left">Species Group</label>
                                </div>
                                <div className="form-control-input-detection d-iblock">
                                    <div className="flex-container flex-wrap vtext-middle">
                                        <div className="radio-item">
                                            <span>
                                                <input id="rdSpeciesGrpDolphin" type="radio" className="radio-button" name="speciesGroup" value={"0"}
                                                    checked={detectionContext.Detection.speciesGroup ? detectionContext.Detection.speciesGroup === '0' : true}
                                                    onChange={handleOptionChange}
                                                    disabled={detectionContext.Detection.biologicalSound !== 'True'} />
                                            </span>
                                            <label htmlFor="rdSpeciesGrpDolphin">Dolphin</label>
                                        </div>
                                        <div className="radio-item">
                                            <span>
                                                <input id="rdSpeciesGrpWhale" type="radio" className="radio-button" name="speciesGroup" value={"1"}
                                                    checked={detectionContext.Detection.speciesGroup === '1'}
                                                    onChange={handleOptionChange}
                                                    disabled={detectionContext.Detection.biologicalSound !== 'True'} />
                                            </span>
                                            <label htmlFor="rdSpeciesGrpWhale">Baleen Whale</label>
                                        </div>
                                        <div className="radio-item">
                                            <span>
                                                <input id="rdSpeciesGrpMultiple" type="radio" className="radio-button" name="speciesGroup" value={"2"}
                                                    checked={detectionContext.Detection.speciesGroup === '2'}
                                                    onChange={handleOptionChange}
                                                    disabled={detectionContext.Detection.biologicalSound !== 'True'} />
                                            </span>
                                            <label htmlFor="rdSpeciesGrpMultiple">Multiple Animals</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ms-2 me-2"><hr className="detection-detail-line-break" /></div>
                            <div className="detection-title tooltip mt-2 mb-2 ms-3 me-3">
                                <span className="vtext-middle">Additional Comments</span>
                                <span className="tooltip-icon tooltip-link vtext-middle" onMouseMove={handleMouseMove}>
                                    <i className="fa-regular fa-circle-question"></i>
                                </span>
                                <span className="tooltip-text ms-1" style={{ top: tooltipPosition.y - 15 }}>Any additional comments you wish to add to support your detection.</span>
                            </div>
                            <div className="ms-3 me-3">
                                <textarea className="detection-textarea" name="comment" placeholder="Provide any additional information that may be useful in understanding the acoustics..." disabled={'True'} value={detectionContext.Detection.comment == null ? '' : detectionContext.Detection.comment} onChange={handleOptionChange} />
                            </div>
                            <div className="detection-button-container ms-3">
                                <button className="btn btn-secondary detection-button" id="btnExport" onClick={exportWavFile}>Export</button>
                                {/*<button className="btn btn-primary detection-button" id="btnSave" onClick={submitValidation}>Save</button>*/}
                            </div>
                        </div>
                        <div id="detection-na-container" className={!detectionContext.Detection.rowIndex ? "text-center ms-3 me-3" : "d-none"}><div className="loading"></div></div>
                        <div className="d-none" id="btnRefresh" onClick={() => getDetection()}></div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ViewDetection;
